import { formatNumberWithAbbreviation } from "@memorang/helpers/common";
import { Trans, useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { LeaderboardAvatar } from "./LeaderboardAvatar";
import { LeaderboardXpAmount } from "./LeaderboardXpAmount";
import type { LeaderboardAvatarType, LeaderboardItem } from "./types";

type Props = LeaderboardItem & {
	xpGap: number;
	avatarType?: LeaderboardAvatarType;
	numUsersToDisplay?: number;
};

export const LeaderboardUserPositionView = ({
	rank,
	username,
	xp,
	xpGap,
	avatar,
	avatarType,
	numUsersToDisplay = 10,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	const gapFormatted = formatNumberWithAbbreviation(xpGap);

	const gapHint =
		xpGap > 0 && rank > 1 ? (
			<Text variant="labelSmall" style={{ color: theme.colors.secondary }}>
				<Trans
					i18nKey={
						rank <= numUsersToDisplay
							? "leaderboard.footer.gapHintToPass"
							: "leaderboard.footer.gapHintFromPodium"
					}
					values={
						rank <= numUsersToDisplay
							? { value: gapFormatted, rank: rank - 1 }
							: { value: gapFormatted }
					}
					components={{
						num: (
							<Text
								variant="labelSmall"
								style={{ color: theme.colors.primary }}
							>
								{""}
							</Text>
						),
						unit: (
							<Text
								variant="labelSmall"
								style={{ color: theme.colors.primary }}
							>
								{""}
							</Text>
						),
						rest: (
							<Text
								variant="labelSmall"
								style={{ color: theme.colors.secondary }}
							>
								{""}
							</Text>
						),
					}}
				/>
			</Text>
		) : null;

	return (
		<Box>
			<Box
				flexDirection="row"
				alignItems="center"
				justifyContent="space-between"
				paddingBottom={8}
			>
				<Text
					variant="labelSmall"
					style={[styles.positionLabel, { color: theme.colors.primary }]}
				>
					{t("leaderboard.yourPosition")}
				</Text>
				{gapHint}
			</Box>
			<Box
				flexDirection="row"
				alignItems="center"
				paddingHorizontal={16}
				paddingVertical={12}
				gap={12}
				borderWidth={1}
				borderColor={theme.colors.border}
				borderRadius={theme.roundness * 3}
				backgroundColor={theme.colors.secondaryContainer}
			>
				<Box
					padding={8}
					minWidth={36}
					borderRadius={99}
					backgroundColor={theme.colors.primary}
					alignItems="center"
					justifyContent="center"
				>
					<Text style={[styles.rankText, { color: theme.colors.onPrimary }]}>
						#{rank.toLocaleString()}
					</Text>
				</Box>
				<Box borderRadius={99} overflow="hidden">
					<LeaderboardAvatar
						username={username}
						size={36}
						avatarImage={avatar}
						avatarType={avatarType}
					/>
				</Box>
				<Text
					variant="bodyMedium"
					numberOfLines={1}
					style={[styles.username, { color: theme.colors.onSurface }]}
				>
					{username}
				</Text>
				<Box
					flexDirection="row"
					alignItems="flex-end"
					gap={4}
					backgroundColor={theme.colors.primary}
					paddingHorizontal={8}
					paddingVertical={4}
					borderRadius={99}
				>
					<LeaderboardXpAmount
						value={xp}
						numberStyle={{ color: theme.colors.onPrimary }}
						unitStyle={[
							styles.xpLabel,
							{ color: theme.colors.onPrimary, opacity: 0.75 },
						]}
					/>
				</Box>
			</Box>
		</Box>
	);
};

const styles = StyleSheet.create({
	positionLabel: {
		textTransform: "uppercase",
		letterSpacing: 0.5,
	},
	rankText: {
		textAlign: "center",
	},
	username: {
		flex: 1,
	},
	xpLabel: {
		lineHeight: 16,
	},
});
